import type { ProductInfoP2P } from '~esav/types/ProductInfo/ProductInfo';
import { isEqual } from 'date-fns';
import { hash, serialize } from 'ohash';
import { unique } from 'radash';

interface TripsPerPaxAndDay {
    date: Date;
    passenger: ApiSchema['Passenger'];
    apiTrips: ApiSchema['Trip'][];
}

/**
 * Transform API activity booking parts into a product info for P2P journey
 *
 * P2P is special, because we have multiple booking parts (admissions + reservations) for multiple trips
 * that belong together. But there is no clear reference in the API, which trips belong together. All booking
 * parts are flattened in one list, so we don't exactly know which admissions and reservations belong to the same trip.
 *
 * We group them by date, passenger and destination. This way they are grouped back together to the original product.
 * One "product info" item is created for all the parts that belong together.
 *
 * We dont know, which trips belong together in the backend,
 * we can only group them by date and same passengers.
 *
 * 1 ProductInfoP2P equals:
 * - Same day
 * - Same passengers
 * - All trips of that day with those passengers
 *
 * Because every product in the offerFlow creates new (duplicated) customers (even with same name),
 * there is no confusion and it's straightforward to group trips per passenger.
 *
 */
export function transformProductInfosP2P(bookingParts: ApiBookingPart[], response: ApiSchema['BookingResponse'], exchangeableGroups: ExchangeableGroup[]): ProductInfoP2P[] {
    const apiBooking = response?.booking;
    if (!apiBooking) {
        return [];
    }

    // Active trips, sorted by start time
    // Why "active"? We don't include trips that belong to refunded or exchanged admission parts.
    const trips = apiBooking.trips
        ?.filter(t => apiBooking.activeTripIds?.includes(t.id))
        ?.sort((a, b) => zurichDate(a?.startTime).getTime() - zurichDate(b?.startTime).getTime()) || [];

    // Combine trips per passenger and day. Multiple passengers on same train/day will be grouped together.
    // Reduce the list of trips into a list of trips per passenger and day.
    const tripsPerPaxAndDay = trips.reduce<TripsPerPaxAndDay[]>((acc, trip) => {
        const tripBookingParts = bookingParts
            .filter(bp => bp.tripCoverage?.coveredTripIds?.includes(trip.id))
            .filter(bp => bp.status !== 'REFUNDED');
        const passengerIds = tripBookingParts.flatMap(bp => bp.passengerIds);
        const passengers = apiBooking.passengers.filter(p => passengerIds.includes(p.id));
        const date = zurichDay(trip.startTime);

        passengers.forEach((passenger) => {
            let item = acc.find(t => t.passenger.id === passenger.id && isEqual(t.date, date));
            if (!item) {
                acc.push((item = { date, passenger, apiTrips: [] }));
            }
            item.apiTrips.push(trip);
        });

        return acc;
    }, []);

    // Filter out duplicate trip sets (same trips, pax, day)
    const tripsPerPaxAndDayUnique = unique(tripsPerPaxAndDay, item => serialize(item.apiTrips));

    // For each unique set of trips per pax and day, create a productItem.
    const productItems = tripsPerPaxAndDayUnique.map<ProductInfoP2P>(item => mapToProductInfo(item, bookingParts, exchangeableGroups, apiBooking));

    return productItems;
}

/**
 * This maps a "trip-per-pax-and-day" item to a ProductInfoP2P item, which is then used to display the P2P product in the app.
 *
 * As a result you get one P2P product info per trips/passenger/date combination, which is how we want to display it in the app.
 *
 * Example:
 *
 * 4 People go to Joch (with reservation)
 * - 8 admission parts (4 up, 4 down) + 8 reservation parts (4 up, 4 down) --> grouped into 1 product info with 4 passengers (Joch trip)
 *
 * 2 People go to GW first one-way
 * - 2 admission parts (up) --> grouped into 1 product info with 2 passengers (GW trip)
 */
function mapToProductInfo(
    item: TripsPerPaxAndDay,
    bookingParts: ApiBookingPart[],
    exchangeableGroups: ExchangeableGroup[],
    apiBooking: ApiSchema['Booking'],
): ProductInfoP2P {
    const type = 'P2P';

    // Filter out and transform the matching trips. 1 Product item can only have trip up & down.
    const trips = item.apiTrips
        .map((apiTrip) => {
            const coveredLegIds = bookingParts
                .filter(bp => bp.tripCoverage?.coveredTripIds?.includes(apiTrip.id))
                .filter(bp => bp.passengerIds.includes(item.passenger.id))
                .filter(bp => bp.status !== 'REFUNDED')
                .filter(bp => bp.objectType === 'Reservation')
                .flatMap(bp => bp.tripCoverage?.coveredLegIds)
                .filter(Boolean) as string[];

            return transformBookedTrip(apiTrip, coveredLegIds);
        })
        .filter(Boolean) as Trip[];
    const tripUp = trips[0];
    const tripDown = trips[1];
    const dateFrom = tripUp?.departureTime ?? item.date;
    const dateUntil = tripDown?.arrivalTime ?? tripUp?.arrivalTime;

    // Transform the matching passengers (to have names etc.)
    const passengers = apiBooking.passengers
        .filter(p =>
            trips.every(trip =>
                bookingParts
                    .filter(bp => bp.tripCoverage?.coveredTripIds?.includes(trip.id))
                    .some(bp => bp.passengerIds.includes(p.id)),
            ),
        )
        .map(p => transformPassenger(p));

    // Exchangeables:
    // ------------------------------------------------------------
    // Button for reservation exchange functionality is displayed for each product info that has exchangeable trips.
    // So we need to check for each product info if there are exchangeable reservations (up/down) for the passenger of that product info.
    // This is true, if there are any (previously transformed) exchangeble groups that match the product and are ready to exchange.
    const matchingExchangeableGroups = exchangeableGroups.filter((eg) => {
        const matchingTrip = [tripUp, tripDown].some(trip => eg.trip?.id === trip?.id);
        const matchingPassenger = passengers.some(p => eg.exchangeables.some(e => e.passengerIds.includes(p.id)));
        return matchingTrip && matchingPassenger;
    });
    const hasExchangeables = matchingExchangeableGroups.length > 0;
    const canExchange = matchingExchangeableGroups.some(eg => eg.exchangeables.some(e => e.isExchangeable));

    // True if the product is "Reservations" only (no tickets, only seat reservations)
    const isEPROnly = bookingParts.some(bp => bp.objectType === 'Reservation') && !bookingParts.some(bp => bp.objectType === 'Admission');

    // Find the corresponding product config, based on:
    // - Matching destinations of the trips (up and down)
    // - If it's EPR only or not (to match Joch EPR Only config)
    // - It falls back to matching "reverse" so trips with reversed direction (e.g. One-Way downwards from GW First) are also correctly assigned to the product config
    const matchingP2PConfigs = productConfigs
        .filter(c => c.type === 'P2P')
        .filter(c => !isEPROnly || c.hasEPR === 'only');

    // Prefer a direct destination match across all configs, regardless of their order.
    const config = matchingP2PConfigs.find(c =>
        c.destinations.some(d => d.ref === tripUp?.destination.ref)
        && (!tripDown || c.destinations.some(d => d.ref === tripDown?.origin.ref)),
    ) ?? matchingP2PConfigs.find(c =>
        c.destinations.some(d => d.ref === tripUp?.origin.ref)
        && (!tripDown || c.destinations.some(d => d.ref === tripDown?.destination.ref)),
    );

    // - Check if there is a dynamic listingId added to the booking parts (dynamic product)
    // - Else, take the static listingId from the config (static product)
    const listingId = bookingParts.filter(bp =>
        bp.tripCoverage?.coveredTripIds?.includes(tripUp?.id ?? '')
        || bp.tripCoverage?.coveredTripIds?.includes(tripDown?.id ?? ''),
    ).map(bp => bp.listingId)[0] ?? config?.listingId;

    // Group items by this identifier
    const id = hash([
        tripUp?.id,
        tripDown?.id,
        dateFrom.getTime(),
        passengers.map(p => p.id).join(','),
    ]);

    // Find or create (first) product info item
    return {
        id,
        type,
        listingId,
        dateFrom,
        dateUntil,
        passengers,
        tripUp,
        tripDown,
        hasExchangeables,
        canExchange,
    };
}
